/**
 * UMD'ed by Augusto Corvalan 4/30/2014
 * dependencies:
 * dibs.defaultCookieSettings
 * dibs.tracking
 * dibs.user
 * $.publish
 * $.subscribe
 */

'use strict';
var root = global;
var $ = require('dibs-jquery-shim');
var moment = require('moment');
var tracking = require('dibs-tracking');
var md5 = require('crypto-js/md5');
var uri = require('bunsen/helpers/uri');
var get = require('lodash.get');
var User = require('../../global/models/User');
var userDetails = require('../../global/tracking/helpers/userDetails');
var chatUtils = require('../utils/social/chat');

var sessionTracking = module.exports = {};
var timesFired = 0;

var TRADE_PROMO_DATE_FROM = get(uri, 'URL.queryKey.tradePromoDate') || '2017-01-16';
var MS_30_DAYS = 2592000000;

/*
 Threshold being used instead of pub/sub since order of custom vars being fired
 varies.

 Currently, the custom vars being fired are:
 1. @see navCreatedListener

 firedThreshold will equal the number of custom vars being pushed.
 */
var firedThreshold = 1;
var _dibs;
var shouldTrackUserId;
var shouldTrackGuestId;
var eventNames;
var getUserId;
var getGuestId;

require('bunsen/libraries/plugins/jquery.cookie');

eventNames = {
    setSessionCustomVar: 'ga:setSessionCustomVar',
    navCreated: 'nav:created',
    userCustomVarCreated: 'ga:userCustomVarCreated'
};

getUserId = function () {
    var emailToken = User.getEmailToken();

    if (User.loggedIn()) {
        return User.getUserId();
    } else if (emailToken) {
        return emailToken.substr(0, emailToken.indexOf('_'));
    }

    return undefined;
};

/**
 * helper function that returns the guest id if it is set in a cookie it returns
 * the existing guest id and if it is not, a new guest id is set and then returned
 */
getGuestId = function () {
    var guestId = User.getGuestId();
    if (!guestId) {
        User.setNewGuestId();
        guestId = User.getGuestId();
    }
    return guestId;
};

/*
 * Set one of three login/registration states
 */
sessionTracking.getLoginStatus = function () {
    var label;
    if (User.loggedIn()) {
        label = 'Registered and Logged In';
    } else {
        label = User.everLoggedIn() ? 'Registered and Logged Out' : 'Not Registered';
    }
    return label;
};

/**
 * FINDING-3669 thin wrapper around user load, for readability
 * @returns {Promise}
 */
sessionTracking.loadUserProfile = function () {
    return User.load();
};

/**
 * Retrieves currency preference from user profile.
 * Note: Should be called after loadUserProfile to ensure user has been loaded
 * @returns {string}
 */
sessionTracking.getUserCurrency = function () {
    var currency;
    var profile;

    if (User.loggedIn()) {
        if (User.getCurrency()) {
            //accessing through getUserProfile is temporary meant only to work in environments that don't support commonJS
            currency = User.getCurrency();
        } else {
            profile = User.getUserProfile();

            if (profile && profile.get('currency')) {
                currency = profile.get('currency');
            }
        }

        if (currency) {
            //currency is PND in the codebase, needs to be GBP for tracking
            return currency.replace(/PND/, 'GBP') + ' Selected';
        }
    }
    return currency;
};

shouldTrackUserId = function (userId) {
    var customVarName = tracking.getCustomVar(1) ? tracking.getCustomVar(1).name : '';
    return userId && customVarName === 'Guest Id';
};

shouldTrackGuestId = function (guestId) {
    return guestId && !User.everLoggedIn() && !tracking.getCustomVar(1);
};

sessionTracking.onNavCreated = function onNavCreated() {
    var guestId = User.getGuestId();
    var userId = User.getUserId();
    var trackUserId = shouldTrackUserId(userId);
    var trackGuestId = shouldTrackGuestId(guestId);
    var trackObj;

    //TODO JF when masquerading, which user should be tracked here?
    /*
        Analytics may refer to this tracking as:
            - "visitor tracking" not to be confused with visitor.tracking.js...
            - or "custom variable 1"
        name: "UserId", value: userId
        - User is registered and logged in OR logged out (userId is in an __utm cookie)
        name: "GuestId", value, guestId
        - User is not registered and logged out
     */
    if (trackUserId || trackGuestId) {
        trackObj = {
            slot: 1,
            name: (userId ? 'User' : 'Guest') + ' Id',
            value: userId ? userId : guestId,
            scope: 1
        };

        tracking = tracking || _dibs.tracking;
        tracking.setCustomVar(trackObj);
        if (trackUserId) {
            $.publish(eventNames.userCustomVarCreated);
        }
    }
};

/**
 * returns the buyerStatus (whether or not the user has any orders)
 */
sessionTracking.getTransactionsStatus = function getTransactionsStatus() {
    var deferred = $.Deferred();

    userDetails.fetchTransactionsStatus().then(function (response) {
        deferred.resolve(response || {});
    }, function () {
        deferred.resolve({});
    });

    return deferred.promise();
};

/**
 * grabs the first session date from the set cookie and returns the first session date
 * in the form Month_Year (i.e. December_2013)
 * @return string|undefined
 */
sessionTracking.getFirstSessionDate = function getFirstSessionDate() {
    var sessions = $.cookie('__utma');
    var firstSession;
    if (sessions) {
        sessions = sessions.split('.');
        firstSession = parseInt(sessions[2], 10) * 1000;
        return moment(firstSession).format('MMMM_YYYY');
    }
    return undefined;
};

function inLast30Days(date) {
    return new Date(date).getTime() > (new Date().getTime() - MS_30_DAYS);
}

function getGaTradeStatus(payload) {
    var tradeStatus = payload.tradeStatus;
    var buyerStatusKey = payload.transactionsStatus.buyerStatusKey;
    var isVerifiedTradeBuyer = User.getCurrentUser().isVerifiedTradeBuyer();
    var userId = User.getUserId();
    var tradeTestGroup = '';

    // check buyer status
    var noneConfirmed = ['SUBMITTED', 'INITIATED', 'NONE'].indexOf(buyerStatusKey) > -1;

    // if trade is created later than date and has no confirmed orders
    if (isVerifiedTradeBuyer && tradeStatus.date && moment(tradeStatus.date) > moment(TRADE_PROMO_DATE_FROM) && noneConfirmed) {
        var secontToLastIdNumber = Number.parseInt(userId.slice(-2, -1));
        // userId ends with 4 or 5
        if ([4, 5].indexOf(secontToLastIdNumber) > -1) {
            tradeTestGroup = '|onboarding promo: control';
        } else {
            tradeTestGroup = '|onboarding promo: variant';
        }
    } else {
        var verifiedInLast30Days = inLast30Days(tradeStatus.verifiedDate) ? 'y' : 'n';
        tradeTestGroup = '|verified_in_last_30_days=' + verifiedInLast30Days;
    }

    return tradeStatus.status && tradeStatus.status + tradeTestGroup;
}

sessionTracking.trackUserAttributes = function () {
    var isLoggedIn = User.loggedIn();
    var userEmail = User.getEmail(true);

    return $.when(sessionTracking.getTransactionsStatus(), User.handleEmailToken())
        .always(function (transactionsStatus) {
            var tradeStatus = userDetails.getTradeStatus();
            tracking.addUserAttributes({
                'loginStatus': isLoggedIn ? 'Logged In' : 'Logged Out',
                'registrationStatus': User.everLoggedIn() ? 'Registered' : 'Not Registered',
                'userID': getUserId(),
                'guestID': isLoggedIn ? undefined : getGuestId(),
                'userEmail': userEmail ? md5(userEmail).toString() : undefined,
                'userEmailAddress': isLoggedIn ? userEmail : undefined,
                'emailToken': User.getEmailToken(),
                'userZip': User.getZipCode(),
                'defaultCurrency': sessionTracking.getUserCurrency() || 'USD Default',
                'firstSessionDate': sessionTracking.getFirstSessionDate(),
                'contactDealerStatus': transactionsStatus.contactDealerStatus,
                'buyerStatus': transactionsStatus.buyerStatus,
                'firstPurchasePrice': transactionsStatus.firstPurchasePrice || 'na',
                'firstPurchaseDate': transactionsStatus.firstPurchaseDate || 'na',
                'tradeStatus': getGaTradeStatus({tradeStatus: tradeStatus, transactionsStatus: transactionsStatus}),
                'customerType': isLoggedIn ? User.getCustomerType() : User.everLoggedIn() ? undefined : 'Unknown'
            });

            chatUtils.customAttribute.transactionsStatus(transactionsStatus);
            chatUtils.customAttribute.tradeStatus(tradeStatus.status);
            if (User.getCurrentUser().isVerifiedTradeBuyer()) {
                chatUtils.customAttribute.verifiedTradeDate(tradeStatus.date);
            }
        });
};

sessionTracking.init = function sessionTrackingInit() {
    if (!root.jasmine) {
        $.subscribe(eventNames.setSessionCustomVar, function () {

            $.publish('ga:blockPageview');
            sessionTracking.loadUserProfile().always(function () {
                var trackObj = {
                    slot: 3,
                    name: sessionTracking.getLoginStatus(),
                    value: sessionTracking.getUserCurrency() || 'USD Default',
                    scope: 2
                };

                tracking = tracking || _dibs.tracking;
                tracking.setCustomVar(trackObj);
                $.publish('ga:unblockPageview');

                // Tracking Session for Google Tag Manager
                sessionTracking.trackUserAttributes();
            });

            //TRANACT-2538 if we're setting up tracking on guest Id, we can set the custom var immediately
            //rather than waiting for user info to load
            if (shouldTrackGuestId(User.getGuestId())) {
                $.publish(eventNames.navCreated);
            }
        });
    }

    /**
     * @name navCreatedListener
     */
    $.subscribe(eventNames.navCreated, function () {
        sessionTracking.onNavCreated();
    });

    $.subscribe(eventNames.userCustomVarCreated, function () {
        timesFired += 1;
        tracking = tracking || _dibs.tracking;
        if (timesFired >= firedThreshold) {
            tracking.fireDummyEvent();
        }
    });
};



//////////////////
// WEBPACK FOOTER
// ./assets/js/global/core/user.tracking.js
// module id = 1978
// module chunks = 63